export * from './access-control'
export * from './app-detection'
export * from './cache-sorting'
export * from './context-checks'
export * from './cross-tab-communication'
export * from './file-transformations'
export * from './keycloak'
export * from './retry'
export * from './status-utils'
export * from './storage'
export * from './text-utils'
export * from './url-normalizer'
export * from './url-utils'
export * from './use-breakpoints'
export * from './use-folder-deletion-listener'
export * from './use-global-folder-deletion-listener'
export * from './use-page-focus-refetch'
export * from './use-url-params'

/**
 * Robust cookie clearing function that handles various cookie scenarios
 */
function clearAllCookies(): void {
  try {
    // Get all cookies and clear them systematically
    const cookies = document.cookie.split(';')

    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=')
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

      if (name) {
        // Try multiple approaches to clear the cookie
        const cookieOptions = [
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`,
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`,
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`,
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname.split('.').slice(-2).join('.')}`,
        ]

        cookieOptions.forEach(option => {
          try {
            document.cookie = option
          } catch (e) {
            // Ignore errors for individual cookie clearing attempts
          }
        })
      }
    })
  } catch (error) {
    console.warn('Error clearing cookies:', error)
  }
}

/**
 * Enhanced force logout function that ensures complete cleanup before redirect
 */
export function forceLogout(withRedirect: boolean = true): void {
  try {
    console.log('🔄 Starting force logout process...')

    // Clear all storage
    localStorage.clear()
    sessionStorage.clear()

    // Clear cookies with multiple attempts
    clearAllCookies()

    // Additional cleanup for any remaining auth-related data
    try {
      // Clear any remaining auth tokens from various storage mechanisms
      if (window.location.hostname.includes('localhost')) {
        // For local development, also try to clear cookies with localhost domain
        document.cookie.split(';').forEach(cookie => {
          const eqPos = cookie.indexOf('=')
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

          if (name) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=localhost`
          }
        })
      }

      // Try to clear any indexedDB or other storage mechanisms
      if ('indexedDB' in window) {
        try {
          indexedDB.deleteDatabase('qse-auth')
          indexedDB.deleteDatabase('qse-storage')
        } catch (e) {
          // Ignore indexedDB errors
        }
      }

      // Clear any service worker registrations
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister()
          })
        })
      }
    } catch (e) {
      console.warn('Error in additional cleanup:', e)
    }

    // Force a small delay to ensure cleanup is complete
    if (withRedirect) {
      console.log('🔄 Cleanup complete, redirecting to login...')

      // Use a small timeout to ensure all cleanup operations complete
      setTimeout(() => {
        try {
          // Force a hard redirect to ensure complete page reload
          window.location.replace('/auth/login')
        } catch (error) {
          console.error('Failed to redirect:', error)
          // Fallback to regular location change
          window.location.href = '/auth/login'
        }
      }, 100)
    } else {
      console.log('✅ Force logout completed without redirect')
    }
  } catch (error) {
    console.error('Error during force logout:', error)

    // Even if there's an error, try to redirect as fallback
    if (withRedirect) {
      try {
        window.location.href = '/auth/login'
      } catch (redirectError) {
        console.error('Failed to redirect after logout error:', redirectError)
      }
    }
  }
}

/**
 * Utility function to trigger authentication change event
 * Call this after successful login to immediately trigger WebSocket connection
 */
export const triggerAuthChange = () => {
  console.log('🚀 Triggering auth change event for immediate WebSocket connection...')
  window.dispatchEvent(new CustomEvent('auth-changed'))
}

/**
 * Enhanced logout function that provides better user experience
 * Use this for normal logout scenarios (not force logout)
 */
export function enhancedLogout(redirectTo: string = '/auth/login'): void {
  try {
    console.log('🔄 Starting enhanced logout process...')

    // Clear storage
    localStorage.clear()
    sessionStorage.clear()

    // Clear cookies
    clearAllCookies()

    // Redirect with a small delay to ensure cleanup
    setTimeout(() => {
      try {
        window.location.href = redirectTo
      } catch (error) {
        console.error('Failed to redirect:', error)
        // Fallback to replace
        window.location.replace(redirectTo)
      }
    }, 100)
  } catch (error) {
    console.error('Error during enhanced logout:', error)
    // Fallback redirect
    window.location.href = redirectTo
  }
}
